import type { CreateUserInput } from '@idc-forms/schemas'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CircleOff, CirclePlay, Info, KeyRound, Pencil, ShieldCheck, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { showApiTokenError } from '@/features/account/api-tokens/api-token-error'
import useAuth from '@/features/auth/hooks/use-auth'
import { staffSessionV2Service } from '@/features/auth/staff-session-v2.service'
import { Permission } from '@/features/roles/consts/permissions'
import { DetailFieldRow } from '@/shared/components/detail-rows/detail-field-row'
import { UserChip } from '@/shared/components/detail-rows/user-chip'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { FEATURES } from '@/shared/config/features'
import { queryKeys } from '@/shared/constants/query-keys'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/accordion'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shared/ui/alert-dialog'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar'
import { Badge } from '@/shared/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { formatHumanDateTime } from '@/shared/utils/format-date'
import {
  useDeleteUserMutation,
  useResetUserPasswordMutation,
  useUpdateUserMutation
} from '../hooks/use-user-mutations'
import type { UserAdminRow } from '../models/user-admin'
import { UserForm } from './user-form'
import { TypeAccountBadge, userInitials } from './users-admin-columns'

type UserDetailPanelProps = {
  user: UserAdminRow
}

/**
 * Panel de detalle del usuario: modo VISTA (resumen + acciones + tab "Más
 * información" con acordeones) y modo FORMULARIO in-place (mismo UserForm que
 * la creación). Credenciales fuera del alcance: son self-service.
 */
export function UserDetailPanel({ user }: UserDetailPanelProps) {
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmPasswordReset, setConfirmPasswordReset] = useState(false)
  const [confirmMfaPolicy, setConfirmMfaPolicy] = useState(false)
  const { auth } = useAuth()
  const queryClient = useQueryClient()
  const closePanel = useSidePanelStore((s) => s.closePanel)
  const updateMutation = useUpdateUserMutation()
  const deleteMutation = useDeleteUserMutation()
  const resetPasswordMutation = useResetUserPasswordMutation()
  const { has, isAdmin } = usePermissions()
  const mfaPolicyMutation = useMutation({
    mutationFn: async (required: boolean) => {
      if (!auth?.v2AccessToken || !user.publicId) throw new Error('Missing v2 staff identity')
      return staffSessionV2Service.updateStaffMfaPolicy(auth.v2AccessToken, user.publicId, {
        required
      })
    },
    onSuccess: async (policy) => {
      setConfirmMfaPolicy(false)
      toast.success(policy.required ? '2FA ahora es obligatorio.' : '2FA ahora es opcional.')
      await queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
    }
  })

  const handleUpdate = (values: CreateUserInput) => {
    updateMutation.mutate(
      { id: user._id, payload: values },
      {
        onSuccess: () => {
          setMode('view')
        }
      }
    )
  }

  const toggleActive = () => {
    updateMutation.mutate({ id: user._id, payload: { active: !user.active } })
  }

  if (mode === 'edit') {
    return (
      <UserForm
        defaultValues={{
          fullname: user.fullname,
          username: user.username,
          email: user.email,
          typeAccount: user.typeAccount,
          roles: user.roles.map((role) => role._id),
          avatar: user.avatar ?? undefined,
          active: user.active
        }}
        submitLabel="Guardar cambios"
        pending={updateMutation.isPending}
        onSubmit={handleUpdate}
        onCancel={() => {
          setMode('view')
        }}
      />
    )
  }

  return (
    <div className="flex flex-col">
      <SidePanelSection>
        <SidePanelSectionBody className="gap-3">
          <div className="flex items-center gap-3">
            <Avatar seed={user._id} className="h-11 w-11">
              {user.avatar && <AvatarImage src={user.avatar} alt="" />}
              <AvatarFallback>{userInitials(user.fullname)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <h3 className="truncate text-base font-semibold leading-tight">{user.fullname}</h3>
              <p className="truncate text-sm text-muted-foreground">@{user.username}</p>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {/* Filas especiales por tipo de dato: identificadores/correo copiables. */}
            <DetailFieldRow label="Correo" value={user.email} copyValue={user.email} />
            <DetailFieldRow label="Usuario" value={user.username} copyValue={user.username} />
            <DetailFieldRow
              label="Tipo de cuenta"
              value={<TypeAccountBadge typeAccount={user.typeAccount} />}
            />
            <DetailFieldRow label="Activo" value={user.active ? 'Sí' : 'No'} />
            <DetailFieldRow
              label="2FA"
              value={user.isTwoFactorAuthenticationEnabled ? 'Habilitado' : 'No habilitado'}
            />
            <DetailFieldRow
              label="2FA obligatorio"
              value={
                user.typeAccount === 'admin'
                  ? 'Sí, por tipo de cuenta'
                  : user.mfaRequired
                    ? 'Sí, por política administrativa'
                    : 'No'
              }
            />
          </div>
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection>
        <SidePanelActions
          maxInline={2}
          actions={[
            ...(has(Permission.UpdateUsers)
              ? [
                  {
                    label: 'Editar usuario',
                    icon: <Pencil className="h-4 w-4" />,
                    variant: 'default' as const,
                    primary: true,
                    onClick: () => {
                      setMode('edit')
                    }
                  },
                  {
                    label: user.active ? 'Desactivar usuario' : 'Activar usuario',
                    icon: user.active ? (
                      <CircleOff className="h-4 w-4" />
                    ) : (
                      <CirclePlay className="h-4 w-4" />
                    ),
                    variant: 'outline' as const,
                    disabled: updateMutation.isPending,
                    onClick: toggleActive
                  }
                ]
              : []),
            // Solo administradores (espeja el AdminGuard del backend): la acción
            // queda auditada con quién la accionó.
            ...(isAdmin
              ? [
                  {
                    label: 'Renovar contraseña',
                    icon: <KeyRound className="h-4 w-4" />,
                    variant: 'outline' as const,
                    disabled: resetPasswordMutation.isPending,
                    onClick: () => {
                      setConfirmPasswordReset(true)
                    }
                  }
                ]
              : []),
            ...(isAdmin && FEATURES.webAuthV2 && user.typeAccount !== 'admin' && user.publicId
              ? [
                  {
                    label: user.mfaRequired ? 'Dejar 2FA opcional' : 'Exigir 2FA',
                    icon: <ShieldCheck className="h-4 w-4" />,
                    variant: 'outline' as const,
                    disabled: mfaPolicyMutation.isPending,
                    onClick: () => {
                      setConfirmMfaPolicy(true)
                    }
                  }
                ]
              : []),
            ...(has(Permission.DeleteUsers)
              ? [
                  {
                    label: 'Eliminar usuario',
                    icon: <Trash2 className="h-4 w-4" />,
                    variant: 'destructive' as const,
                    disabled: deleteMutation.isPending,
                    onClick: () => {
                      setConfirmDelete(true)
                    }
                  }
                ]
              : [])
          ]}
        />
      </SidePanelSection>

      <Tabs defaultValue="more-info">
        <TabsList className="w-full justify-normal rounded-none">
          <TabsTrigger className="flex gap-1" value="more-info">
            <Info className="size-4" /> Más información
          </TabsTrigger>
        </TabsList>
        <TabsContent value="more-info">
          <Accordion type="multiple" defaultValue={['roles']}>
            <AccordionItem value="roles">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Roles
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-wrap gap-1.5 pt-2 pl-3 pr-2">
                  {user.roles.length > 0 ? (
                    user.roles.map((role) => (
                      <Badge key={role._id} variant="secondary">
                        {role.name}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-sm text-muted-foreground">Sin roles asignados</span>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="activity">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Actividad
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow label="Creado" value={formatHumanDateTime(user.createdAt)} />
                  <DetailFieldRow
                    label="Creado por"
                    value={<UserChip name={user.createdBy?.fullname} />}
                  />
                  <DetailFieldRow label="Actualizado" value={formatHumanDateTime(user.updatedAt)} />
                  <DetailFieldRow
                    label="Actualizado por"
                    value={<UserChip name={user.updatedBy?.fullname} />}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmPasswordReset} onOpenChange={setConfirmPasswordReset}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Renovar contraseña</AlertDialogTitle>
            <AlertDialogDescription>
              Se generará una contraseña nueva para “{user.fullname}” y se enviará a {user.email}.
              La contraseña actual dejará de funcionar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                resetPasswordMutation.mutate(user._id)
              }}
            >
              Renovar y enviar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmMfaPolicy} onOpenChange={setConfirmMfaPolicy}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {user.mfaRequired ? 'Dejar 2FA opcional' : 'Exigir 2FA'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {user.mfaRequired
                ? `“${user.fullname}” podrá mantener 2FA activo voluntariamente, pero ya no será una política obligatoria.`
                : `“${user.fullname}” tendrá que activar 2FA antes de usar funciones protegidas. Sus sesiones sin verificar quedarán restringidas.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={mfaPolicyMutation.isPending}
              onClick={(event) => {
                event.preventDefault()
                mfaPolicyMutation.mutate(!user.mfaRequired, {
                  onError: (error) => showApiTokenError(error)
                })
              }}
            >
              {user.mfaRequired ? 'Dejar opcional' : 'Exigir 2FA'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar usuario</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará “{user.fullname}” (@{user.username}). Su usuario y correo quedarán libres
              para un alta nueva; el histórico que creó se conserva.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteMutation.mutate(user._id, {
                  onSuccess: () => {
                    closePanel()
                  }
                })
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
